import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Replay as RetryIcon,
  Delete as DeleteIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authedEventSource } from '../lib/authedEventSource';
import { useSnackbar } from 'notistack';
import { SERVER_BASE_URL } from '../config';
import { chartDataService } from '../services/chartDataService';

const API_BASE_URL = SERVER_BASE_URL;

interface ImportJob {
  id: string;
  song: string;
  artist: string;
  youtube_url?: string;
  status: string;
  video_id?: string;
  youtube_download_path?: string;
  horizontal_s3_url?: string;
  vertical_s3_url?: string;
  error_message?: string;
  retry_count: number;
  created_at: string;
  updated_at: string;
  started_processing_at?: string;
  completed_at?: string;
  yt_download_job_id?: string;
  sheet_row_index?: number;
}

type SortField = 'created_at' | 'artist' | 'song' | 'status';
type SortOrder = 'asc' | 'desc';

const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
  switch (status) {
    case 'pending':
      return 'default';
    case 'downloading':
      return 'info';
    case 'crop_needed':
      return 'warning';
    case 'processing_h':
    case 'processing_v':
    case 'generating_horizontal':
    case 'generating_vertical':
      return 'primary';
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'downloading':
      return 'Downloading';
    case 'crop_needed':
      return 'Crop Needed';
    case 'processing_h':
      return 'Processing H';
    case 'processing_v':
      return 'Processing V';
    case 'generating_horizontal':
      return 'Generating Horizontal';
    case 'generating_vertical':
      return 'Generating Vertical';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
};

export const ImportManager: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const eventSourceRef = useRef<EventSource | null>(null);
  const sseJobIdsRef = useRef<string>(''); // Track which job IDs we have SSE listeners for
  const recoveringJobsRef = useRef<Set<string>>(new Set()); // Track jobs currently being recovered to prevent duplicates

  // Multi-select mode state
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());

  // Function to update the Master Video List (DB) with S3 URLs by song+artist
  const updateVideoManagerUrls = useCallback(async (song: string, artist: string, horizontalUrl: string, verticalUrl: string) => {
    try {
      console.log(`[ImportManager] Updating Master Video List for "${artist}" - "${song}"`);

      // Upsert/update the matching video by (song, artist) — the DB resolves the row.
      const updated = await chartDataService.updateVideoByKey(song, artist, {
        s3Url: horizontalUrl,
        verticalUrl,
      });

      if (!updated) {
        console.error('[ImportManager] No matching video found in Master Video List');
        enqueueSnackbar(`Video processed but not found in Video Manager: "${artist}" - "${song}"`, { variant: 'warning' });
        return;
      }

      console.log('[ImportManager] Successfully updated Master Video List');
      enqueueSnackbar(`✅ Video Manager updated: "${artist}" - "${song}"`, { variant: 'success' });

    } catch (error) {
      console.error('[ImportManager] Error updating Video Manager:', error);
      enqueueSnackbar('Failed to update Video Manager', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/import-manager/jobs`);

      if (!response.ok) {
        throw new Error('Failed to fetch import jobs');
      }

      const data = await response.json();

      if (data.success) {
        setJobs(data.jobs || []);
      } else {
        throw new Error(data.error || 'Failed to fetch jobs');
      }
    } catch (err) {
      console.error('[ImportManager] Error fetching jobs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchJobs, 10000);
    return () => clearInterval(interval);
  }, []);

  // Check YouTube download status via the authed Billboard server (which holds the
  // downloader secret) — the browser never talks to the downloader directly.
  const checkYouTubeDownloadStatus = useCallback(async (job: ImportJob) => {
    if (!job.yt_download_job_id) return;

    // Skip if this job is already being recovered (prevent duplicate requests)
    if (recoveringJobsRef.current.has(job.id)) {
      console.log(`[ImportManager] Job ${job.id} is already being recovered, skipping`);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/youtube-job-status/${job.yt_download_job_id}`);
      if (!response.ok) return;

      const data = await response.json();
      console.log(`[ImportManager] YouTube API status for job ${job.id}:`, data.status, data);

      // Extract download URL - YouTube API can return it at top level or inside result object
      const downloadUrl = data.download_url || data.result?.download_url;
      const filename = data.filename || data.result?.filename || 'video.mp4';
      const title = data.title || data.result?.title || `${job.artist} - ${job.song}`;

      // If YouTube download completed but our job is still "downloading", trigger the completion flow
      if (data.status === 'completed' && downloadUrl) {
        console.log(`[ImportManager] YouTube download completed for job ${job.id}, triggering completion flow`);
        console.log(`[ImportManager] Download URL: ${downloadUrl}`);

        // Call the backend to download the file and update status to crop_needed
        const completeResponse = await fetch(`${API_BASE_URL}/api/youtube-download-complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ytDownloadJobId: job.yt_download_job_id,
            downloadUrl: downloadUrl,
            filename: filename,
            title: title
          })
        });

        if (completeResponse.ok) {
          enqueueSnackbar(`Download complete for "${job.artist} - ${job.song}"`, { variant: 'success' });
          // Refresh the job list to show updated status
          fetchJobs();
        } else {
          const errorData = await completeResponse.json().catch(() => ({}));
          console.error(`[ImportManager] Failed to complete download:`, errorData);
          // If the error is "Unknown ytDownloadJobId", the in-memory map was cleared
          // We need to handle this by directly updating the job status
          if (errorData.error === 'Unknown ytDownloadJobId') {
            console.log(`[ImportManager] Job mapping lost, using recovery endpoint`);

            // Mark job as being recovered to prevent duplicate requests
            recoveringJobsRef.current.add(job.id);

            // Call recovery endpoint that downloads file using job data from database
            try {
              const recoveryResponse = await fetch(`${API_BASE_URL}/api/youtube-download-recover`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  importJobId: job.id,
                  downloadUrl: downloadUrl,
                  filename: filename,
                  title: title
                })
              });

              if (recoveryResponse.ok) {
                enqueueSnackbar(`Download recovered for "${job.artist} - ${job.song}"`, { variant: 'success' });
                fetchJobs();
              } else {
                console.error(`[ImportManager] Recovery also failed`);
              }
            } finally {
              // Remove from recovering set when done (success or failure)
              recoveringJobsRef.current.delete(job.id);
            }
          }
        }
      }
      // If YouTube download failed, update our database
      else if (data.status === 'failed') {
        console.log(`[ImportManager] YouTube download failed for job ${job.id}, updating database`);

        const updateResponse = await fetch(`${API_BASE_URL}/api/import-manager/jobs/${job.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            status: 'failed',
            error_message: data.error || 'YouTube download failed'
          })
        });

        if (updateResponse.ok) {
          enqueueSnackbar(`Download failed for "${job.artist} - ${job.song}"`, { variant: 'error' });
          // Refresh the job list to show updated status
          fetchJobs();
        }
      }
    } catch (error) {
      console.error(`[ImportManager] Error checking YouTube download status:`, error);
    }
  }, [enqueueSnackbar]);

  // Check downloading jobs for completion or failures
  // This handles cases where the user navigated away during download and the SSE listener was lost
  useEffect(() => {
    const downloadingJobs = jobs.filter(job =>
      job.status === 'downloading' && job.yt_download_job_id
    );

    if (downloadingJobs.length === 0) return;

    console.log(`[ImportManager] Checking ${downloadingJobs.length} downloading job(s) for status updates`);
    downloadingJobs.forEach(job => checkYouTubeDownloadStatus(job));
  }, [jobs, checkYouTubeDownloadStatus]);

  // SSE listener for Video Manager updates
  // Using refs and memoized values to prevent constant reconnection
  const processingJobsKey = React.useMemo(() => {
    const processingJobs = jobs.filter(job =>
      job.video_id && (
        job.status === 'processing_h' ||
        job.status === 'generating_horizontal' ||
        job.status === 'generating_vertical'
      )
    );
    // Create a stable key based on video_ids of processing jobs
    return processingJobs.map(j => j.video_id).sort().join(',');
  }, [jobs]);

  useEffect(() => {
    // Only set up SSE listeners if we have jobs in processing states with video_id
    const processingJobs = jobs.filter(job =>
      job.video_id && (
        job.status === 'processing_h' ||
        job.status === 'generating_horizontal' ||
        job.status === 'generating_vertical'
      )
    );

    // Skip if no processing jobs or if we already have listeners for these exact jobs
    if (processingJobs.length === 0) {
      sseJobIdsRef.current = '';
      return;
    }

    const currentKey = processingJobs.map(j => j.video_id).sort().join(',');
    if (currentKey === sseJobIdsRef.current) {
      // Already have listeners for these exact jobs, skip
      return;
    }

    console.log(`[ImportManager] Setting up SSE listeners for ${processingJobs.length} processing job(s)`);
    sseJobIdsRef.current = currentKey;

    // Create EventSource for each processing job
    const eventSources: EventSource[] = [];

    processingJobs.forEach(job => {
      const videoId = job.video_id!; // We know it exists from the filter above

      (async () => {
        const eventSource = await authedEventSource(`${API_BASE_URL}/api/progress/${videoId}`);

        eventSource.addEventListener('video_manager_update', (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('[ImportManager] Received video_manager_update event:', data);

            const { song, artist, horizontalUrl, verticalUrl } = data;

            if (song && artist && horizontalUrl && verticalUrl) {
              // Update Video Manager with the URLs
              updateVideoManagerUrls(song, artist, horizontalUrl, verticalUrl);
            }
          } catch (error) {
            console.error('[ImportManager] Error handling video_manager_update event:', error);
          }
        });

        eventSource.onerror = () => {
          // SSE errors are expected when jobs complete or server closes connection
          console.log(`[ImportManager] SSE connection closed for job ${job.id}`);
          eventSource.close();
        };

        eventSources.push(eventSource);
      })();
    });

    // Cleanup function
    return () => {
      console.log('[ImportManager] Cleaning up SSE listeners');
      sseJobIdsRef.current = '';
      eventSources.forEach(es => es.close());
    };
    // Using processingJobsKey as the only dependency to prevent unnecessary re-runs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingJobsKey, updateVideoManagerUrls]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleRetry = async (jobId: string) => {
    try {
      // Find the job to get its details
      const job = jobs.find(j => j.id === jobId);
      if (!job) {
        throw new Error('Job not found');
      }

      if (!job.youtube_url) {
        throw new Error('No YouTube URL available for retry');
      }

      // Delete the old failed job
      const deleteResponse = await fetch(`${API_BASE_URL}/api/import-manager/jobs/${jobId}`, {
        method: 'DELETE',
      });

      if (!deleteResponse.ok) {
        throw new Error('Failed to delete old job');
      }

      // Trigger the download directly - this will create a new import job and start downloading
      const downloadResponse = await fetch(`${API_BASE_URL}/api/download-youtube`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          youtubeUrl: job.youtube_url,
          song: job.song,
          artist: job.artist,
          sheetRowIndex: job.sheet_row_index
        })
      });

      if (!downloadResponse.ok) {
        const errorData = await downloadResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to start download');
      }

      enqueueSnackbar(`Retrying "${job.artist} - ${job.song}"`, { variant: 'info' });
      await fetchJobs();
    } catch (err) {
      console.error('[ImportManager] Error retrying job:', err);
      setError(err instanceof Error ? err.message : 'Failed to retry job');
    }
  };

  const handleDelete = async (jobId: string) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/import-manager/jobs/${jobId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete job');
      }

      await fetchJobs();
    } catch (err) {
      console.error('[ImportManager] Error deleting job:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete job');
    }
  };

  // Multi-select handlers
  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode(prev => {
      if (!prev) {
        // Entering multi-select mode: select all jobs automatically
        setSelectedJobIds(new Set(jobs.map(job => job.id)));
      } else {
        // Exiting multi-select mode: clear selections
        setSelectedJobIds(new Set());
      }
      return !prev;
    });
  };

  const toggleJobSelection = (jobId: string) => {
    setSelectedJobIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const selectAllJobs = () => {
    setSelectedJobIds(new Set(jobs.map(job => job.id)));
  };

  const deselectAllJobs = () => {
    setSelectedJobIds(new Set());
  };

  const handleDeleteSelected = async () => {
    if (selectedJobIds.size === 0) {
      enqueueSnackbar('No jobs selected', { variant: 'info' });
      return;
    }

    if (!window.confirm(`Delete ${selectedJobIds.size} selected job(s)?`)) {
      return;
    }

    try {
      const deletePromises = Array.from(selectedJobIds).map(jobId =>
        fetch(`${API_BASE_URL}/api/import-manager/jobs/${jobId}`, {
          method: 'DELETE',
        })
      );

      await Promise.all(deletePromises);
      enqueueSnackbar(`Deleted ${selectedJobIds.size} job(s)`, { variant: 'success' });
      setSelectedJobIds(new Set());
      setIsMultiSelectMode(false);
      await fetchJobs();
    } catch (err) {
      console.error('[ImportManager] Error deleting selected jobs:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete selected jobs');
    }
  };

  const handleClearCompleted = async () => {
    const completedCount = jobs.filter(j => j.status === 'completed' || j.status === 'failed').length;
    if (completedCount === 0) {
      enqueueSnackbar('No completed or failed jobs to clear', { variant: 'info' });
      return;
    }

    if (!window.confirm(`Clear ${completedCount} completed/failed jobs?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/import-manager/jobs/clear-completed`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to clear completed jobs');
      }

      const data = await response.json();
      enqueueSnackbar(`Cleared ${data.deletedCount} jobs`, { variant: 'success' });
      await fetchJobs();
    } catch (err) {
      console.error('[ImportManager] Error clearing completed jobs:', err);
      setError(err instanceof Error ? err.message : 'Failed to clear completed jobs');
    }
  };

  const handleRowClick = (job: ImportJob) => {
    console.log('[ImportManager] Row clicked:', job.status, job.song, 'youtube_url:', job.youtube_url);

    if (job.status === 'crop_needed' || job.status === 'generating' || job.status === 'generating_horizontal' || job.status === 'generating_vertical') {
      // Navigate to Upload Video for cropping or to see generation progress
      navigate(`/upload-video?jobId=${job.id}`);
    } else if (job.status === 'pending' || job.status === 'downloading') {
      // Navigate to Upload Video to show download progress
      // Use jobId as primary method (more reliable), youtube_url as fallback for context
      const params = new URLSearchParams({
        jobId: job.id,
        song: job.song,
        artist: job.artist,
      });
      if (job.youtube_url) {
        params.set('youtubeUrl', job.youtube_url);
      }
      navigate(`/upload-video?${params.toString()}`);
    } else if (job.status === 'completed') {
      // Navigate to Video Manager with player open for this song
      const params = new URLSearchParams({
        player: 'true',
        song: job.song,
        artist: job.artist,
      });
      navigate(`/video-manager?${params.toString()}`);
    }
  };

  const sortedJobs = [...jobs].sort((a, b) => {
    let aVal: any;
    let bVal: any;

    if (sortField === 'created_at') {
      aVal = new Date(a.created_at || 0).getTime();
      bVal = new Date(b.created_at || 0).getTime();
    } else if (sortField === 'artist') {
      aVal = a.artist || '';
      bVal = b.artist || '';
    } else if (sortField === 'song') {
      aVal = a.song || '';
      bVal = b.song || '';
    } else if (sortField === 'status') {
      aVal = a.status || '';
      bVal = b.status || '';
    }

    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'left', fontWeight: 'medium' }}>
          Import Manager
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/video-manager')}
            sx={{
              color: '#000000',
              borderColor: '#000000',
              '&:hover': {
                borderColor: '#000000',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Video Manager
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/')}
            sx={{
              color: '#000000',
              borderColor: '#000000',
              '&:hover': {
                borderColor: '#000000',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Charts
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ marginBottom: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, marginBottom: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={handleClearCompleted}
          size="small"
          color="error"
          disabled={jobs.filter(j => j.status === 'completed' || j.status === 'failed').length === 0}
        >
          Clear Completed
        </Button>
        <IconButton onClick={fetchJobs} size="small">
          <RefreshIcon />
        </IconButton>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ width: '100%' }}>
          <TableContainer component={Paper} sx={{ border: '1px solid #ddd', borderRadius: '4px' }}>
            {sortedJobs.length > 0 ? (
              <Table sx={{ minWidth: 650 }} aria-label="import jobs table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: isMultiSelectMode ? 180 : 80 }}>
                      {isMultiSelectMode ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="Select All">
                            <IconButton size="small" onClick={selectAllJobs}>
                              <SelectAllIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deselect All">
                            <IconButton size="small" onClick={deselectAllJobs}>
                              <DeselectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Selected">
                            <IconButton
                              size="small"
                              onClick={handleDeleteSelected}
                              color="error"
                              disabled={selectedJobIds.size === 0}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Exit Selection Mode">
                            <IconButton size="small" onClick={toggleMultiSelectMode}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Tooltip title="Select Multiple">
                          <IconButton size="small" onClick={toggleMultiSelectMode}>
                            <CheckBoxOutlineBlankIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortField === 'created_at'}
                        direction={sortField === 'created_at' ? sortOrder : 'asc'}
                        onClick={() => handleSort('created_at')}
                      >
                        Created
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortField === 'artist'}
                        direction={sortField === 'artist' ? sortOrder : 'asc'}
                        onClick={() => handleSort('artist')}
                      >
                        Artist
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortField === 'song'}
                        direction={sortField === 'song' ? sortOrder : 'asc'}
                        onClick={() => handleSort('song')}
                      >
                        Song
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortField === 'status'}
                        direction={sortField === 'status' ? sortOrder : 'asc'}
                        onClick={() => handleSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedJobs.map((job, index) => {
                    const isClickable = job.status === 'crop_needed' || job.status === 'completed' || job.status === 'pending' || job.status === 'downloading' || job.status === 'generating' || job.status === 'generating_horizontal' || job.status === 'generating_vertical';
                    return (
                    <TableRow
                      key={job.id}
                      onClick={() => handleRowClick(job)}
                      sx={{
                        backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                        cursor: isClickable ? 'pointer' : 'default',
                        '&:hover': {
                          backgroundColor: isClickable ? 'rgba(255, 230, 0, 0.15)' : 'rgba(0, 0, 0, 0.08)',
                        },
                      }}
                    >
                      <TableCell>
                        {isMultiSelectMode ? (
                          <Checkbox
                            checked={selectedJobIds.has(job.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleJobSelection(job.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                          />
                        ) : (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {job.status === 'failed' && (
                              <Tooltip title="Retry">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRetry(job.id);
                                  }}
                                >
                                  <RetryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(job.id);
                                }}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(job.created_at)}</TableCell>
                      <TableCell>{job.artist}</TableCell>
                      <TableCell>{job.song}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(job.status)}
                          color={getStatusColor(job.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {job.error_message && (
                          <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                            Error: {job.error_message}
                          </Typography>
                        )}
                        {job.retry_count > 0 && (
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Retries: {job.retry_count}
                          </Typography>
                        )}
                        {job.youtube_url && (
                          <Typography variant="caption" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <a href={job.youtube_url} target="_blank" rel="noopener noreferrer">
                              YouTube Link
                            </a>
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Box sx={{ padding: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No import jobs found
                </Typography>
              </Box>
            )}
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};
